// SAM-1309 (U11): the name in the header. A user with a business shows it; the estate group roles (no business) show
// their estate group when the server scopes the list to exactly one; everyone else shows the tenant. The app's own
// name is the last resort, only while nothing else is known.
import { useQuery } from '@tanstack/react-query';
import { defaultInstance } from '../api/httpClient';
import { useBusinessContext } from '../context/BusinessContext';
import { useUserPermissions } from './useUserPermissions';
import { useTenantBranding } from './useTenantBranding';

export const pickHeaderTitle = (o: {
  businessName?: string | null;
  estateGroupName?: string | null;
  tenantName?: string | null;
  fallback: string;
}): string => o.businessName || o.estateGroupName || o.tenantName || o.fallback;

/** The name of the estate group of an AEG / FEG: the one group their list holds, else none (the tenant is shown). */
export const onlyEstateGroupName = (groups: Array<{ name?: string | null }> | undefined): string | null =>
  groups && groups.length === 1 && groups[0].name ? groups[0].name : null;

export const useHeaderTitle = (fallback: string): string => {
  const { business } = useBusinessContext();
  const { isEstateGroupAdmin, isEstateGroupFrontDesk, cognitoId } = useUserPermissions();
  const { tenantName } = useTenantBranding();
  const isEstateGroupRole = Boolean(isEstateGroupAdmin || isEstateGroupFrontDesk);
  const { data } = useQuery({
    // Per user: logging out does not clear the query cache, so the next user in the same tab must not read this one's group.
    queryKey: ['header-estate-group', cognitoId ?? null],
    enabled: isEstateGroupRole && Boolean(cognitoId) && !business?.name,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Array<{ name?: string | null }>> => {
      const response = await defaultInstance({ method: 'GET', url: '/estate_group?limit=5' });
      return (response as any)?.data?.data ?? (response as any)?.data ?? [];
    },
  });
  return pickHeaderTitle({
    businessName: business?.name,
    estateGroupName: isEstateGroupRole ? onlyEstateGroupName(data) : null,
    tenantName,
    fallback,
  });
};
